import { useAuthState } from "_ReactContext/AuthContext";
import { useApiCache } from "customHooks/useApi";
import { extractValue } from "./helpers";

/**
 * Hook to get list of all available tests
 */
export const useGetTestList = () => {
    const { company } = useAuthState();

    const listAddress = `/test/list`;
    const postData = {
        companyId: company.companyId,
        pageSize: 50000
    };
    const [apiReturn] = useApiCache(listAddress, "post", postData, false);
    return apiReturn;
};

/**
 * Hook to get list of test templates
 */
export const useGetTestTemplates = () => {
    const { company } = useAuthState();

    const postTemplateList = `/test/testcompleotemplatelist`;
    const [apiReturn] = useApiCache(
        postTemplateList,
        "post",
        {
            companyId: company.companyId,
            fieldsToReturn: [
                "name",
                "pk",
                "skGS1pk",
                "testType",
                "possibleResults"
            ]
        },
        false,
        1000 * 60 * 5
    );
    return apiReturn;
};

/**
 * Hook to get available tests and templates for a specific assessment
 * Returns only tests/templates that haven't been added yet
 */
export const useAvailableTestsAndTemplates = (existingTestNames: string[]) => {
    const testListResult = useGetTestList();
    const templateListResult = useGetTestTemplates();

    const listData: CompleoShared.Assessments.ITest[] =
        testListResult?.response?.data?.data || [];
    const templateList: CompleoShared.Assessments.ITestTemplate[] =
        templateListResult?.response?.data?.data || [];

    // Filter out tests that are already added
    const availableTests = listData.filter(
        (test) => !existingTestNames.includes(test.name)
    );

    // Filter out templates that are already added
    const availableTemplates = templateList.filter(
        (template) => !existingTestNames.includes(template.name)
    );

    // Format for dropdown options
    const testOptions = availableTests.map((test) => ({
        label: test.name,
        value: test.skGS1pk,
        testType: test.testType,
        possibleResults: test.possibleResults,
        templateUUID: undefined,
        fullData: test
    }));

    const templateOptions = availableTemplates.map((template) => {
        const uuidNew = extractValue(template.skGS1pk, "TESTTEMPLATEID") || "";
        return {
            label: template.name,
            value: `TESTID:${uuidNew}`,
            testType: template.testType,
            possibleResults: template.possibleResults,
            templateUUID: uuidNew,
            fullData: template
        };
    });

    const ready =
        testListResult?.status === "success" &&
        templateListResult?.status === "success";

    return {
        testOptions,
        templateOptions,
        availableTests,
        availableTemplates,
        ready
    };
};
